import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { buildOpenTarget, detectMobilePlatform } from '@/lib/app-download-prompt';
import { track } from '@/lib/analytics';

const normalizeUrl = (url: string | null | undefined): string | null => {
  const trimmed = String(url ?? '').trim();
  if (!trimmed || trimmed === 'null') return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : null;
};

const PlayStoreIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="currentColor" aria-hidden="true">
    <path d="M3.6 2.3c-.4.2-.6.6-.6 1.1v17.2c0 .5.2.9.6 1.1l.1.1 9.6-9.6v-.2L3.7 2.2l-.1.1zm12.7 9.5-2.7-2.7L4.4 1.9l12 12c.9-.5.9-1.6-.1-2.1zm0 .4-12 12 9.2-9.2 2.7-2.7c1-.5 1-1.6.1-2.1zM4.4 22.1l9.2-9.2 2.7 2.7-11.8 6.7c-.2.1-.4.1-.6 0l.5-.2z" />
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="currentColor" aria-hidden="true">
    <path d="M17.05 12.54c-.03-2.89 2.36-4.27 2.47-4.34-1.35-1.97-3.44-2.24-4.18-2.27-1.78-.18-3.47 1.05-4.37 1.05-.9 0-2.29-1.02-3.77-1-1.94.03-3.72 1.13-4.72 2.86-2.01 3.49-.51 8.66 1.45 11.5.96 1.39 2.1 2.95 3.6 2.89 1.45-.06 2-.93 3.74-.93s2.24.93 3.77.9c1.56-.03 2.55-1.41 3.5-2.8 1.1-1.61 1.55-3.17 1.58-3.25-.03-.02-3.03-1.16-3.07-4.61zM14.16 4.06c.8-.97 1.34-2.32 1.19-3.66-1.15.05-2.55.77-3.38 1.74-.74.86-1.39 2.23-1.22 3.55 1.29.1 2.6-.65 3.41-1.63z" />
  </svg>
);

/**
 * Permanent store buttons (Google Play / App Store) shown in the sidebar
 * dialog and the Help page. Hidden inside the native app; each button only
 * renders when its store URL is published in system settings.
 */
export function AppStoreButtons() {
  const { t } = useTranslation();
  const { settings } = useSystemSettings();

  if (Capacitor.isNativePlatform()) return null;

  const androidUrl = normalizeUrl(settings.app_store_url_android);
  const iosUrl = normalizeUrl(settings.app_store_url_ios);
  if (!androidUrl && !iosUrl) return null;

  const platform = detectMobilePlatform(navigator.userAgent);

  const open = (target: 'android' | 'ios') => {
    const storeUrl = target === 'android' ? androidUrl : iosUrl;
    if (!storeUrl) return;
    track('app_download_prompt_clicked', { source: 'menu', platform: target });
    window.location.href = buildOpenTarget(platform ?? target, storeUrl);
  };

  return (
    <div className="flex flex-col gap-2">
      {androidUrl && (
        <Button variant="outline" className="w-full justify-start gap-3 h-12" onClick={() => open('android')}>
          <PlayStoreIcon />
          <span className="flex flex-col items-start leading-tight">
            <span className="text-[11px] text-muted-foreground">{t('appDownload.getItOn')}</span>
            <span className="font-semibold">Google Play</span>
          </span>
        </Button>
      )}
      {iosUrl && (
        <Button variant="outline" className="w-full justify-start gap-3 h-12" onClick={() => open('ios')}>
          <AppleIcon />
          <span className="flex flex-col items-start leading-tight">
            <span className="text-[11px] text-muted-foreground">{t('appDownload.downloadOn')}</span>
            <span className="font-semibold">App Store</span>
          </span>
        </Button>
      )}
    </div>
  );
}
